import { apiClient } from "./ApiClient";
import type { SupportedLocale } from "../i18n";

interface UserMeResponse {
  data: {
    lang?: string;
    default_week?: {
      default_office_id?: number | null;
    };
    [key: string]: unknown;
  };
}

interface DayDeclaration {
  status_day_id: number | null;
  office_id: number | null;
}

interface UserDaysResponse {
  data: DayDeclaration[];
}

// status_day_id mapping from Deskare API
const STATUS_OFFICE = 1;

const SUPPORTED_LOCALES: SupportedLocale[] = ["en", "fr", "it", "de", "es"];

let cachedMePromise: Promise<UserMeResponse> | null = null;

function fetchUserMe(): Promise<UserMeResponse> {
  if (!cachedMePromise) {
    cachedMePromise = apiClient
      .get<UserMeResponse>("/api/v1/users/me")
      .finally(() => {
        cachedMePromise = null;
      });
  }
  return cachedMePromise;
}

export class UserService {
  static async getUserLang(): Promise<SupportedLocale | null> {
    try {
      const response = await fetchUserMe();
      const lang = response.data?.lang?.toLowerCase().slice(0, 2);
      if (lang && SUPPORTED_LOCALES.includes(lang as SupportedLocale)) {
        return lang as SupportedLocale;
      }
      return null;
    } catch {
      return null;
    }
  }

  /**
   * Returns the office ID the user should see by default:
   * 1. The office from their day declaration (if declared at an office today)
   * 2. Their default_office_id from default_week
   * 3. null (caller should fall back to first office in list)
   */
  static async getUserDefaultOfficeId(): Promise<string | null> {
    try {
      const today = new Date().toISOString().split("T")[0];

      const [daysResponse, meResponse] = await Promise.all([
        apiClient
          .get<UserDaysResponse>("/api/v1/users/me/days", { date: today })
          .catch(() => null),
        fetchUserMe(),
      ]);

      // Priority 1: Today's declaration with status "office" and a specific office_id
      if (daysResponse?.data) {
        const declarations = Array.isArray(daysResponse.data)
          ? daysResponse.data
          : [];
        const officeDeclaration = declarations.find(
          (d) => d.status_day_id === STATUS_OFFICE && d.office_id != null
        );
        if (officeDeclaration?.office_id != null) {
          return officeDeclaration.office_id.toString();
        }
      }

      // Priority 2: Default office from user profile
      const defaultOfficeId = meResponse.data?.default_week?.default_office_id;
      if (defaultOfficeId != null) {
        return defaultOfficeId.toString();
      }

      return null;
    } catch {
      return null;
    }
  }
}
